import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAlarm } from "../hooks/test/useAlarm";

interface FrequentlyUsedFeature {
  title: string;
  description: string;
  path: string;
  buttonText: string;
}

const GuidePage: React.FC = () => {
  const navigate = useNavigate();

  const frequentlyUsedFeatures: FrequentlyUsedFeature[] = [
    {
      title: "곡 업데이트 일괄 알림",
      description:
        "여러 곡이 한번에 업데이트된 경우, 사용자들에게 알림을 보냅니다. BigQuery로 데이터를 조회하고 알림을 발송합니다.",
      path: "/bigQuery",
      buttonText: "빅쿼리 페이지로 이동",
    },
    {
      title: "신청곡 관리",
      description: "유저 신청곡을 확인하고 제조 리스트에 추가합니다.",
      path: "/songRequest2",
      buttonText: "신청곡 페이지로 이동",
    },
  ];

  return (
    <Container>
      <Title>어드민 페이지 가이드</Title>

      <Section>
        <SectionTitle>⚠️ 필독사항</SectionTitle>
        <ImportantBox>
          <li>문제 발생시 순서대로 시도해주세요:</li>
          <ol>
            <li>페이지 새로고침</li>
            <li>캐시 삭제 (상단 버튼 또는 직접 삭제)</li>
          </ol>
        </ImportantBox>
      </Section>

      <Section>
        <SectionTitle>🎯 자주 사용하는 기능</SectionTitle>
        <FeatureGrid>
          {frequentlyUsedFeatures.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
              <Button onClick={() => navigate(feature.path)}>
                {feature.buttonText}
              </Button>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </Section>

      <Section>
        <SectionTitle>📝 최근 업데이트된 기능</SectionTitle>
        <UpdateList>
          <UpdateItem>
            <UpdateTitle>곡 업데이트 알림 시스템</UpdateTitle>
            <li>아티스트별 알림 추가</li>
            <li>BigQuery 데이터 조회</li>
            <li>업데이트 알림 일괄 전송</li>
          </UpdateItem>

          <UpdateItem>
            <UpdateTitle>신청곡 관리 시스템</UpdateTitle>
            <li>[New] 신청곡, [New] 제조중 페이지 추가</li>
            <li>'making에 추가' 버튼으로 제조중 페이지 이동</li>
            <li>제조중 페이지에서 곡 제목/아티스트 선택</li>
            <li>'가수-곡제목' 형식 클립보드 복사</li>
          </UpdateItem>

          <UpdateItem>
            <UpdateTitle>오디오 파일 처리</UpdateTitle>
            <li>파일 업로드시 정규화 적용</li>
            <li>1~2곡씩 업로드 (대용량은 새로고침 후 재시도)</li>
            <li>완료 버튼으로 파일 업로드, DB 업데이트, 알림 전송</li>
          </UpdateItem>

          <UpdateItem>
            <UpdateTitle>썸네일 관리</UpdateTitle>
            <li>경로: homebrew-prod/artistThumbnail/아티스트명</li>
            <li>썸네일 추가 후 캐시삭제 및 새로고침</li>
          </UpdateItem>
        </UpdateList>
      </Section>
    </Container>
  );
};
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.5em;
  color: #2c3e50;
  margin-bottom: 40px;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 1.8em;
  color: #34495e;
  margin-bottom: 20px;
  border-bottom: 2px solid #eee;
  padding-bottom: 10px;
`;

const ImportantBox = styled.ul`
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 20px 40px;
  margin-bottom: 30px;

  li {
    margin-bottom: 10px;
    font-size: 1.1em;
  }

  ol {
    margin-top: 10px;
    li {
      margin-left: 20px;
    }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-5px);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.4em;
  color: #2c3e50;
  margin-bottom: 15px;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const Button = styled.button`
  background-color: #3498db;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #2980b9;
  }
`;

const UpdateList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const UpdateItem = styled.ul`
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  li {
    margin-bottom: 8px;
    color: #666;
    line-height: 1.4;
  }
`;

const UpdateTitle = styled.h4`
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.2em;
`;

export default GuidePage;
